<template>
  <div class="execution-detail-page">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading execution details...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <div class="error-icon">⚠️</div>
      <h2>Failed to Load Execution</h2>
      <p>{{ error }}</p>
      <div class="error-actions">
        <button @click="loadExecutionDetails" class="btn-primary">Try Again</button>
        <button @click="$router.push('/admin/scout/workflow/history')" class="btn-secondary">
          Back to History
        </button>
      </div>
    </div>

    <div v-else-if="execution" class="execution-content">
      <!-- Header -->
      <div class="execution-header">
        <button @click="$router.push('/admin/scout/workflow/history')" class="btn-back">
          ← Back to History
        </button>
        
        <!-- Workflow control buttons (only for running/paused workflows) -->
        <div v-if="isRunningWorkflow" class="workflow-controls">
          <button 
            v-if="execution.status === 'running'"
            @click="confirmPauseWorkflow"
            class="btn-control pause"
            title="Pause Workflow"
          >
            <i class="bi bi-pause-circle"></i> Pause
          </button>
          <button 
            v-if="execution.status === 'paused'"
            @click="resumeWorkflow"
            class="btn-control resume"
            title="Resume Workflow"
          >
            <i class="bi bi-play-circle"></i> Resume
          </button>
          <button 
            @click="confirmKillWorkflow"
            class="btn-control kill"
            title="Kill Workflow"
          >
            <i class="bi bi-x-circle"></i> Kill
          </button>
        </div>
        
        <!-- Restart buttons (only for errored/killed workflows) -->
        <div v-if="execution.status === 'errored' || execution.status === 'killed'" class="workflow-controls">
          <button 
            @click="confirmRestartSmart"
            class="btn-control restart-smart"
            title="Smart Restart (preserve completed nodes)"
          >
            <i class="bi bi-arrow-clockwise"></i> Smart Restart
          </button>
          <button 
            @click="confirmRestartFresh"
            class="btn-control restart-fresh"
            title="Fresh Restart (clear all states)"
          >
            <i class="bi bi-arrow-repeat"></i> Fresh Restart
          </button>
        </div>
        
        <!-- Fresh restart button for completed workflows -->
        <div v-if="execution.status === 'completed'" class="workflow-controls">
          <button 
            @click="confirmRestartFresh"
            class="btn-control restart-fresh"
            title="Fresh Restart (clear all states)"
          >
            <i class="bi bi-arrow-repeat"></i> Fresh Restart
          </button>
        </div>
      </div>

      <!-- Main Info Card -->
      <div class="info-card">
        <div class="info-card-header">
          <div>
            <h1>{{ execution.workflow_name }}</h1>
            <div class="workflow-meta">
              <span class="workflow-uuid">
                <span class="uuid-label">Execution UUID:</span>
                {{ execution.execution_uuid }}
                <CopyButton :text="execution.execution_uuid" />
              </span>
              <span v-if="execution.context_uuid" class="context-uuid">
                <span class="uuid-label">Context UUID:</span>
                {{ execution.context_uuid }}
                <CopyButton :text="execution.context_uuid" />
              </span>
            </div>
          </div>
          <StatusBadge :status="execution.status" size="large" />
        </div>

        <div class="info-grid">
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-clock-history"></i>
              Duration
            </span>
            <span class="info-value">{{ formatDuration(execution.duration) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-hdd-stack"></i>
              Environment
            </span>
            <span class="info-value">
              <span :class="`env-badge env-${execution.env}`">
                {{ execution.env.toUpperCase() }}
              </span>
            </span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-play-circle"></i>
              Started
            </span>
            <span class="info-value">{{ formatTimestamp(execution.start_time) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-stop-circle"></i>
              Ended
            </span>
            <span class="info-value">{{ formatTimestamp(execution.end_time) }}</span>
          </div>
          <div class="info-item">
            <span class="info-label">
              <i class="bi bi-tag"></i>
              Type
            </span>
            <span class="info-value">{{ execution.type }}</span>
          </div>
          <div v-if="execution.workflow_uuid" class="info-item">
            <span class="info-label">
              <i class="bi bi-fingerprint"></i>
              Workflow UUID
            </span>
            <span class="info-value uuid-text">
              {{ truncateUuid(execution.workflow_uuid, 16) }}
              <CopyButton :text="execution.workflow_uuid" />
            </span>
          </div>
        </div>
      </div>

      <!-- Error Message (if failed) -->
      <div v-if="execution.error_message" class="error-card">
        <h3><i class="bi bi-exclamation-triangle-fill"></i> Execution Error</h3>
        <pre>{{ execution.error_message }}</pre>
      </div>

      <!-- Input Data -->
      <div class="section-card">
        <div class="section-header" @click="toggleSection('input')">
          <h3>
            <i class="bi bi-chevron-right" :class="{ 'rotated': sections.input }"></i>
            <i class="bi bi-braces"></i>
            Input Data
          </h3>
        </div>
        <div v-if="sections.input" class="section-content json-viewer-wrapper">
          <JsonViewer :value="execution.input_data" :expand-depth="1" copyable sort></JsonViewer>
        </div>
      </div>

      <!-- Node Execution Timeline -->
      <!-- Node Timeline -->
      <div class="section-card">
        <div class="section-header">
          <h3>
            <i class="bi bi-diagram-3"></i>
            Node Execution Timeline
          </h3>
          <span class="node-count">
            <i class="bi bi-boxes"></i>
            {{ filteredNodes.length }} / {{ sortedNodes.length }} nodes
          </span>
        </div>
        <div class="section-content">
          <div class="node-search-bar">
            <i class="bi bi-search"></i>
            <input 
              type="text" 
              v-model="nodeSearchQuery" 
              placeholder="Search nodes by name, type, action, status..."
              class="node-search-input"
            />
            <button 
              v-if="nodeSearchQuery" 
              @click="nodeSearchQuery = ''"
              class="clear-search-btn"
              title="Clear search"
            >
              <i class="bi bi-x-circle"></i>
            </button>
          </div>
          <div class="nodes-container">
            <template v-for="nodeItem in filteredNodes">
              <!-- Load More Placeholder -->
              <div 
                v-if="nodeItem.isLoadMorePlaceholder"
                :key="nodeItem.uniqueKey"
                class="load-more-placeholder"
                :style="{ 'padding-left': (nodeItem.depth * 24) + 'px' }"
              >
                <button 
                  @click="loadMoreChildren(nodeItem.parentUniqueKey)"
                  class="btn-load-more"
                >
                  <i class="bi bi-plus-circle"></i>
                  Load {{ Math.min(5, nodeItem.remainingCount) }} more 
                  ({{ nodeItem.remainingCount }} remaining of {{ nodeItem.totalChildren }} total)
                </button>
              </div>
              
              <!-- Regular Node Card -->
              <NodeCard 
                v-else
                :key="nodeItem.uniqueKey"
                :node="nodeItem.node"
                :node-unique-key="nodeItem.uniqueKey"
                :depth="nodeItem.depth"
                :has-children="nodeItem.children.length > 0"
                :children-count="nodeItem.childrenCount"
                :show-controls="isRunningWorkflow"
                :workflow-status="execution.status"
                @pause-node="confirmPauseNode"
                @resume-node="resumeNode"
                @kill-node="confirmKillNode"
                @toggle-children="toggleNodeChildren"
              />
            </template>
          </div>
        </div>
      </div>      <!-- Execution Logs -->
      <div v-if="execution.logs" class="section-card">
        <div class="section-header">
          <h3>
            <i class="bi bi-file-text"></i>
            Execution Logs
          </h3>
          <span class="log-count">
            <i class="bi bi-list-ul"></i>
            {{ execution.logs.length }} entries
          </span>
        </div>
        <div class="section-content">
          <LogViewer :logs="execution.logs" />
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <ConfirmModal
      :show="confirmModal.show"
      :title="confirmModal.title"
      :message="confirmModal.message"
      :confirm-text="confirmModal.confirmText"
      :danger-button="confirmModal.danger"
      :icon="confirmModal.icon"
      @confirm="handleConfirmAction"
      @cancel="closeConfirmModal"
    />
  </div>
</template>

<script>
import WorkflowHistory from '@/lib/WorkflowHistory';
import StatusBadge from '@/components/workflow/StatusBadge.vue';
import CopyButton from '@/components/workflow/CopyButton.vue';
import NodeCard from '@/components/workflow/NodeCard.vue';
import LogViewer from '@/components/workflow/LogViewer.vue';
import ConfirmModal from '@/components/workflow/ConfirmModal.vue';
import JsonViewer from 'vue-json-viewer';
import { 
  formatDuration, 
  formatTimestamp, 
  truncateUuid 
} from '@/shared/workflowHistoryUtils';

export default {
  name: 'WorkflowExecutionDetail',
  components: {
    StatusBadge,
    CopyButton,
    NodeCard,
    LogViewer,
    ConfirmModal,
    JsonViewer
  },
  data() {
    return {
      execution: null,
      loading: false,
      error: null,
      workflowHistoryService: null,
      collapsedNodes: new Set(),
      nodeSearchQuery: '',
      paginatedNodes: new Map(), // Map of parentNodeKey -> { page: number, perPage: number }
      sections: {
        input: false
      },
      confirmModal: {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'question',
        action: null,
        params: null
      }
    };
  },
  computed: {
    sortedNodes() {
      if (!this.execution || !this.execution.execution_nodes) return [];
      
      // Build a hierarchical tree structure
      const nodesMap = new Map();
      const rootNodes = [];
      
      // First pass: create all nodes with depth and children array
      // Use index as unique key since nodes can have same id but different parent_node_id
      this.execution.execution_nodes.forEach((nodeItem, index) => {
        const uniqueKey = `${nodeItem.node.id}-${nodeItem.parent_node_id || 'root'}-${index}`;
        nodesMap.set(uniqueKey, {
          ...nodeItem,
          uniqueKey,
          children: [],
          depth: 0
        });
      });
      
      // Create a map to find children by parent_node_id
      const nodesByParentId = new Map();
      this.execution.execution_nodes.forEach((nodeItem, index) => {
        const uniqueKey = `${nodeItem.node.id}-${nodeItem.parent_node_id || 'root'}-${index}`;
        const parentKey = nodeItem.parent_node_id;
        if (!nodesByParentId.has(parentKey)) {
          nodesByParentId.set(parentKey, []);
        }
        nodesByParentId.get(parentKey).push({ nodeItem, uniqueKey });
      });
      
      // Second pass: build parent-child relationships and calculate depth
      this.execution.execution_nodes.forEach((nodeItem, index) => {
        const uniqueKey = `${nodeItem.node.id}-${nodeItem.parent_node_id || 'root'}-${index}`;
        const node = nodesMap.get(uniqueKey);
        
        if (nodeItem.parent_node_id) {
          // Find parent node by matching node.id === parent_node_id
          let parentNode = null;
          for (const value of nodesMap.values()) {
            if (value.node.id === nodeItem.parent_node_id) {
              parentNode = value;
              break;
            }
          }
          
          if (parentNode) {
            parentNode.children.push(node);
            node.depth = parentNode.depth + 1;
          } else {
            rootNodes.push(node);
          }
        } else {
          rootNodes.push(node);
        }
      });
      
      // Count total descendants for each node
      const countDescendants = (node) => {
        let count = node.children.length;
        node.children.forEach(child => {
          count += countDescendants(child);
        });
        node.childrenCount = count;
        return count;
      };
      
      nodesMap.forEach(node => countDescendants(node));
      
      // Flatten tree in hierarchical order (depth-first traversal) with pagination
      const flattenTree = (node, parentUniqueKey = null) => {
        const result = [node];
        // Sort children by start time before flattening
        const sortedChildren = [...node.children].sort((a, b) => {
          const aTime = (a.node.state && a.node.state.starttime) ? new Date(a.node.state.starttime) : new Date(0);
          const bTime = (b.node.state && b.node.state.starttime) ? new Date(b.node.state.starttime) : new Date(0);
          return aTime - bTime;
        });
        
        // If there are many children (>5), apply pagination
        if (sortedChildren.length > 5) {
          const paginationKey = node.uniqueKey;
          const paginationState = this.paginatedNodes.get(paginationKey) || { currentPage: 1, perPage: 5 };
          const startIdx = 0;
          const endIdx = paginationState.currentPage * paginationState.perPage;
          const visibleChildren = sortedChildren.slice(startIdx, endIdx);
          const hasMore = endIdx < sortedChildren.length;
          
          // Add visible children
          visibleChildren.forEach(child => {
            result.push(...flattenTree(child, node.uniqueKey));
          });
          
          // Add a "load more" placeholder if there are more children
          if (hasMore) {
            const remainingCount = sortedChildren.length - endIdx;
            result.push({
              isLoadMorePlaceholder: true,
              parentUniqueKey: node.uniqueKey,
              remainingCount,
              totalChildren: sortedChildren.length,
              currentlyShowing: endIdx,
              depth: node.depth + 1,
              uniqueKey: `load-more-${node.uniqueKey}`
            });
          }
        } else {
          // Show all children if <= 5
          sortedChildren.forEach(child => {
            result.push(...flattenTree(child, node.uniqueKey));
          });
        }
        
        return result;
      };
      
      // Sort root nodes by start time
      const sortedRoots = rootNodes.sort((a, b) => {
        const aTime = (a.node.state && a.node.state.starttime) ? new Date(a.node.state.starttime) : new Date(0);
        const bTime = (b.node.state && b.node.state.starttime) ? new Date(b.node.state.starttime) : new Date(0);
        return aTime - bTime;
      });
      
      // Flatten all trees
      return sortedRoots.flatMap(flattenTree);
    },
    visibleNodes() {
      // Filter nodes based on collapsed state
      return this.sortedNodes.filter(nodeItem => {
        // Always show root nodes
        if (nodeItem.depth === 0) return true;
        
        // Check if any ancestor is collapsed using uniqueKey
        let parentId = nodeItem.parent_node_id;
        
        while (parentId) {
          // Find the parent node's uniqueKey
          const parentNode = this.sortedNodes.find(n => n.node.id === parentId);
          if (parentNode && this.collapsedNodes.has(parentNode.uniqueKey)) {
            return false;
          }
          // Move to next parent
          parentId = parentNode ? parentNode.parent_node_id : null;
        }
        
        return true;
      });
    },
    filteredNodes() {
      // When searching, search all sorted nodes (ignore collapse state)
      // When not searching, return visible nodes (respecting collapse state)
      if (!this.nodeSearchQuery || this.nodeSearchQuery.trim() === '') {
        return this.visibleNodes;
      }
      
      const query = this.nodeSearchQuery.toLowerCase().trim();
      
      return this.sortedNodes.filter(nodeItem => {
        const node = nodeItem.node;
        const state = node.State || node.state || {};
        
        // Search in node name
        if (node.name && node.name.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in node type
        if (node.type && node.type.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in node action
        if (node.action && node.action.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in node status
        if (state.status && state.status.toLowerCase().includes(query)) {
          return true;
        }
        
        // Search in node ID
        if (node.id && node.id.toString().includes(query)) {
          return true;
        }
        
        // Search in expression
        if (node.data && node.data.expression && node.data.expression.toLowerCase().includes(query)) {
          return true;
        }
        
        return false;
      });
    },
    isRunningWorkflow() {
      return this.execution && (this.execution.status === 'running' || this.execution.status === 'paused');
    }
  },
  created() {
    this.workflowHistoryService = new WorkflowHistory(this.$workflowApi);
    this.loadExecutionDetails();
  },
  methods: {
    formatDuration,
    formatTimestamp,
    truncateUuid,
    
    toggleNodeChildren(uniqueKey, isCollapsed) {
      if (isCollapsed) {
        this.collapsedNodes.add(uniqueKey);
      } else {
        this.collapsedNodes.delete(uniqueKey);
      }
      // Force reactivity
      this.collapsedNodes = new Set(this.collapsedNodes);
    },
    
    loadMoreChildren(parentUniqueKey) {
      const currentState = this.paginatedNodes.get(parentUniqueKey) || { currentPage: 1, perPage: 5 };
      const newState = {
        ...currentState,
        currentPage: currentState.currentPage + 1
      };
      this.paginatedNodes.set(parentUniqueKey, newState);
      // Force reactivity
      this.paginatedNodes = new Map(this.paginatedNodes);
    },
    
    async loadExecutionDetails() {
      const executionUuid = this.$route.params.executionUuid;
      
      if (!executionUuid) {
        this.error = 'No execution UUID provided';
        return;
      }
      
      this.loading = true;
      this.error = null;
      
      try {
        // Use auto method that checks running first, then history
        const data = await this.workflowHistoryService.fetchExecutionDetailsAuto(executionUuid);
        this.execution = data;
      } catch (err) {
        if (err.response && err.response.status === 404) {
          this.error = 'Workflow execution not found';
        } else {
          this.error = err.message || 'Failed to load execution details';
        }
        console.error('Error loading execution details:', err);
      } finally {
        this.loading = false;
      }
    },
    
    toggleSection(section) {
      this.sections[section] = !this.sections[section];
    },
    
    formatJson(data) {
      return JSON.stringify(data, null, 2);
    },

    confirmPauseWorkflow() {
      this.confirmModal = {
        show: true,
        title: 'Pause Workflow',
        message: `Are you sure you want to pause the workflow "${this.execution.workflow_name}"? This will pause execution until you resume it.`,
        confirmText: 'Pause Workflow',
        danger: false,
        icon: 'warning',
        action: 'pauseWorkflow',
        params: null
      };
    },

    async pauseWorkflow() {
      try {
        await this.workflowHistoryService.pauseWorkflow(this.execution.execution_uuid);
        // Update status immediately
        this.execution.status = 'paused';
        this.$bvToast.toast('Workflow paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to pause workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error pausing workflow:', error);
      }
    },

    async resumeWorkflow() {
      try {
        await this.workflowHistoryService.resumeWorkflow(this.execution.execution_uuid);
        // Update status immediately
        this.execution.status = 'running';
        this.$bvToast.toast('Workflow resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to resume workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error resuming workflow:', error);
      }
    },

    confirmKillWorkflow() {
      this.confirmModal = {
        show: true,
        title: 'Kill Workflow',
        message: `Are you sure you want to kill workflow "${this.execution.workflow_name}"? This action cannot be undone.`,
        confirmText: 'Kill Workflow',
        danger: true,
        icon: 'danger',
        action: 'killWorkflow',
        params: null
      };
    },

    async killWorkflow() {
      try {
        await this.workflowHistoryService.killWorkflow(this.execution.execution_uuid);
        this.$bvToast.toast('Workflow killed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        // Redirect to history after killing
        setTimeout(() => {
          this.$router.push('/admin/scout/workflow/history');
        }, 1500);
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to kill workflow', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error killing workflow:', error);
      }
    },

    async pauseNode(nodeId) {
      try {
        await this.workflowHistoryService.pauseNode(this.execution.execution_uuid, nodeId);
        // Update node paused flag immediately
        const node = this.execution.final_nodes.find(n => n.id === nodeId);
        if (node) {
          node.paused = true;
        }
        this.$bvToast.toast('Node paused successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to pause node', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error pausing node:', error);
      }
    },

    async resumeNode(nodeId) {
      try {
        await this.workflowHistoryService.resumeNode(this.execution.execution_uuid, nodeId);
        // Update node paused flag immediately
        const node = this.execution.final_nodes.find(n => n.id === nodeId);
        if (node) {
          node.paused = false;
        }
        this.$bvToast.toast('Node resumed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to resume node', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error resuming node:', error);
      }
    },

    confirmPauseNode(nodeId) {
      const node = this.execution.final_nodes.find(n => n.id === nodeId);
      const nodeName = node ? node.name : `Node ${nodeId}`;
      
      this.confirmModal = {
        show: true,
        title: 'Pause Node',
        message: `Are you sure you want to pause the node "${nodeName}"? This will pause execution of this node until you resume it.`,
        confirmText: 'Pause Node',
        danger: false,
        icon: 'warning',
        action: 'pauseNode',
        params: { nodeId }
      };
    },

    confirmKillNode(nodeId) {
      const node = this.execution.final_nodes.find(n => n.id === nodeId);
      const nodeName = node ? node.name : `Node ${nodeId}`;
      
      this.confirmModal = {
        show: true,
        title: 'Kill Node',
        message: `Are you sure you want to kill the node "${nodeName}"? This will also skip all dependent child nodes. This action cannot be undone.`,
        confirmText: 'Kill Node',
        danger: true,
        icon: 'danger',
        action: 'killNode',
        params: { nodeId }
      };
    },

    async killNode(nodeId) {
      try {
        await this.workflowHistoryService.killNode(this.execution.execution_uuid, nodeId);
        this.$bvToast.toast('Node killed successfully', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
        // Reload to show updated status
        await this.loadExecutionDetails();
      } catch (error) {
        this.$bvToast.toast(error.message || 'Failed to kill node', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error killing node:', error);
      }
    },

    handleConfirmAction() {
      if (this.confirmModal.action === 'killWorkflow') {
        this.killWorkflow();
      } else if (this.confirmModal.action === 'killNode') {
        this.killNode(this.confirmModal.params.nodeId);
      } else if (this.confirmModal.action === 'pauseWorkflow') {
        this.pauseWorkflow();
      } else if (this.confirmModal.action === 'pauseNode') {
        this.pauseNode(this.confirmModal.params.nodeId);
      } else if (this.confirmModal.action === 'restartSmart') {
        this.restartWorkflowSmart();
      } else if (this.confirmModal.action === 'restartFresh') {
        this.restartWorkflowFresh();
      }
      this.closeConfirmModal();
    },

    confirmRestartSmart() {
      this.confirmModal = {
        show: true,
        title: 'Smart Restart Workflow',
        message: `Are you sure you want to restart "${this.execution.workflow_name}" with smart mode? This will preserve completed nodes and only retry failed/pending nodes.`,
        confirmText: 'Smart Restart',
        danger: false,
        icon: 'info',
        action: 'restartSmart',
        params: null
      };
    },

    confirmRestartFresh() {
      this.confirmModal = {
        show: true,
        title: 'Fresh Restart Workflow',
        message: `Are you sure you want to restart "${this.execution.workflow_name}" with fresh mode? This will clear all node states and restart the entire workflow from scratch.`,
        confirmText: 'Fresh Restart',
        danger: false,
        icon: 'warning',
        action: 'restartFresh',
        params: null
      };
    },

    async restartWorkflowSmart() {
      try {
        const response = await this.workflowHistoryService.restartWorkflowSmart(this.execution.execution_uuid);
        console.log('Smart restart response:', response);
        
        const newExecutionUuid = response && response.execution_uuid ? response.execution_uuid : null;
        console.log('New execution UUID:', newExecutionUuid);
        
        if (newExecutionUuid) {
          const h = this.$createElement;
          const message = h('div', [
            h('p', { class: 'mb-2' }, 'Workflow execution started (smart mode)'),
            h('b-button', {
              props: { size: 'sm', variant: 'light' },
              on: {
                click: () => {
                  this.$router.push(`/admin/scout/workflow/running/${newExecutionUuid}`);
                  this.$bvToast.hide('workflow-restart-smart-detail');
                }
              }
            }, 'See execution details')
          ]);
          this.$bvToast.toast([message], {
            id: 'workflow-restart-smart-detail',
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } else {
          this.$bvToast.toast('Workflow restarted successfully (smart mode)', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
          setTimeout(() => {
            this.$router.push('/admin/scout/workflow/running');
          }, 1500);
        }
      } catch (error) {
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to restart workflow';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error restarting workflow (smart):', error);
      }
    },

    async restartWorkflowFresh() {
      try {
        const response = await this.workflowHistoryService.restartWorkflowFresh(this.execution.execution_uuid);
        console.log('Fresh restart response:', response);
        
        const newExecutionUuid = response && response.execution_uuid ? response.execution_uuid : null;
        console.log('New execution UUID:', newExecutionUuid);
        
        if (newExecutionUuid) {
          const h = this.$createElement;
          const message = h('div', [
            h('p', { class: 'mb-2' }, 'Workflow execution started (fresh mode)'),
            h('b-button', {
              props: { size: 'sm', variant: 'light' },
              on: {
                click: () => {
                  this.$router.push(`/admin/scout/workflow/running/${newExecutionUuid}`);
                  this.$bvToast.hide('workflow-restart-fresh-detail');
                }
              }
            }, 'See execution details')
          ]);
          this.$bvToast.toast([message], {
            id: 'workflow-restart-fresh-detail',
            title: 'Success',
            variant: 'success',
            solid: true,
            autoHideDelay: 5000
          });
        } else {
          this.$bvToast.toast('Workflow restarted successfully (fresh mode)', {
            title: 'Success',
            variant: 'success',
            solid: true
          });
          setTimeout(() => {
            this.$router.push('/admin/scout/workflow/running');
          }, 1500);
        }
      } catch (error) {
        const errorMessage = error.response && error.response.data && error.response.data.message 
          ? error.response.data.message 
          : 'Failed to restart workflow';
        this.$bvToast.toast(errorMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
        console.error('Error restarting workflow (fresh):', error);
      }
    },

    closeConfirmModal() {
      this.confirmModal = {
        show: false,
        title: '',
        message: '',
        confirmText: 'Confirm',
        danger: false,
        icon: 'question',
        action: null,
        params: null
      };
    }
  }
};
</script>

<style scoped>
.execution-detail-page {
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-primary);
}

.spinner {
  width: 50px;
  height: 50px;
  border: 4px solid var(--border-default);
  border-top-color: var(--primary-start);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  font-size: 64px;
  margin-bottom: 16px;
}

.error-state h2 {
  margin: 0 0 12px 0;
  color: var(--text-primary);
}

.error-state p {
  color: #9ca3af;
  margin: 0 0 24px 0;
}

.error-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
}

.btn-primary,
.btn-secondary {
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
}

.btn-primary {
  background-color: var(--primary-start);
  color: var(--text-primary);
  border: 1px solid var(--primary-start);
}

.btn-primary:hover {
  background-color: hsl(243, 91%, 64%);
}

.btn-secondary {
  background-color: var(--bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-default);
}

.btn-secondary:hover {
  background-color: var(--bg-card);
}

.execution-header {
  margin-bottom: 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
}

.workflow-controls {
  display: flex;
  gap: 8px;
}

.btn-back {
  padding: 8px 16px;
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--text-primary);
}

.btn-back:hover {
  background-color: var(--bg-card);
}

.btn-control {
  background: transparent;
  border: 1px solid #6b7280;
  color: #9ca3af;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-control:hover {
  background-color: rgba(107, 114, 128, 0.1);
  border-color: #9ca3af;
  color: var(--text-primary);
}

.btn-control.pause:hover {
  border-color: #f59e0b;
  color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.1);
}

.btn-control.resume:hover {
  border-color: #10b981;
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.1);
}

.btn-control.kill:hover {
  border-color: #ef4444;
  color: #ef4444;
  background-color: rgba(239, 68, 68, 0.1);
}

.btn-control.restart-smart {
  border-color: #10b981;
  color: #10b981;
}

.btn-control.restart-smart:hover {
  border-color: #10b981;
  color: #10b981;
  background-color: rgba(16, 185, 129, 0.1);
}

.btn-control.restart-fresh {
  border-color: #f59e0b;
  color: #f59e0b;
}

.btn-control.restart-fresh:hover {
  border-color: #f59e0b;
  color: #f59e0b;
  background-color: rgba(245, 158, 11, 0.1);
}

.info-card,
.error-card,
.section-card {
  background: var(--bg-card);
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid var(--border-default);
}

.info-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
}

.info-card-header h1 {
  margin: 0 0 8px 0;
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
}

.workflow-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.workflow-uuid,
.context-uuid {
  display: flex;
  align-items: center;
  gap: 8px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
  color: #9ca3af;
}

.uuid-label {
  font-weight: 600;
  color: var(--primary-start);
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
}

.info-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.info-label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-label i {
  color: var(--primary-start);
  font-size: 14px;
}

.info-value {
  font-size: 16px;
  color: var(--text-primary);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
}

.uuid-text {
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
}

.env-badge {
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.5px;
  display: inline-block;
}

.env-dev {
  background-color: var(--border-default);
  color: #fbbf24;
  border: 1px solid #fbbf24;
}

.env-stage {
  background-color: var(--border-default);
  color: #fb923c;
  border: 1px solid #fb923c;
}

.env-live {
  background-color: var(--border-default);
  color: #10b981;
  border: 1px solid #10b981;
}

.error-card {
  background-color: var(--form-bg);
  border: 2px solid #ef4444;
}

.error-card h3 {
  margin: 0 0 12px 0;
  color: #fca5a5;
  font-size: 18px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-card h3 i {
  color: #ef4444;
}

.error-card pre {
  background-color: var(--bg-card);
  padding: 16px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0;
  color: #fca5a5;
  font-size: 14px;
  border: 1px solid var(--border-default);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  cursor: pointer;
  user-select: none;
}

.section-header h3 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-header h3 i {
  color: var(--primary-start);
}

.section-header h3 i.bi-chevron-right {
  font-size: 12px;
  transition: transform 0.2s ease;
  color: #9ca3af;
}

.section-header h3 i.bi-chevron-right.rotated {
  transform: rotate(90deg);
}

.node-count,
.log-count {
  font-size: 14px;
  color: #9ca3af;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 6px;
}

.node-count i,
.log-count i {
  color: var(--primary-start);
}

.node-search-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  border-radius: 6px;
  margin-bottom: 16px;
}

.node-search-bar i.bi-search {
  color: var(--primary-start);
  font-size: 16px;
}

.node-search-input {
  flex: 1;
  background: transparent;
  border: none;
  color: var(--text-primary);
  font-size: 14px;
  outline: none;
  padding: 4px 8px;
}

.node-search-input::placeholder {
  color: #6b7280;
}

.clear-search-btn {
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  font-size: 16px;
}

.clear-search-btn:hover {
  color: #ef4444;
}

.load-more-placeholder {
  margin: 8px 0;
  display: flex;
  align-items: center;
}

.btn-load-more {
  background: var(--form-bg);
  border: 1px dashed var(--border-default);
  color: var(--text-secondary);
  padding: 12px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
}

.btn-load-more:hover {
  background-color: var(--bg-card);
  border-color: var(--primary-start);
  color: var(--primary-start);
  border-style: solid;
}

.btn-load-more i {
  font-size: 16px;
}

.section-content pre {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  padding: 16px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 0;
  color: var(--text-primary);
  font-size: 13px;
}

.json-viewer-wrapper {
  background-color: var(--form-bg);
  border: 1px solid var(--border-default);
  padding: 16px;
  border-radius: 6px;
  font-family: 'Monaco', 'Menlo', 'Courier New', monospace;
  font-size: 13px;
}

/* Vue JSON Viewer Dark Theme Overrides */
.json-viewer-wrapper >>> .jv-container {
  background-color: var(--form-bg) !important;
  color: var(--text-primary) !important;
}

.json-viewer-wrapper >>> .jv-ellipsis {
  color: #9ca3af !important;
  background-color: var(--border-default) !important;
  padding: 2px 6px !important;
  border-radius: 3px !important;
  cursor: pointer !important;
}

.json-viewer-wrapper >>> .jv-ellipsis:hover {
  background-color: var(--bg-card-secondary) !important;
}

.json-viewer-wrapper >>> .jv-button {
  color: #9ca3af !important;
  background-color: var(--border-default) !important;
  border-radius: 3px !important;
}

.json-viewer-wrapper >>> .jv-button:hover {
  background-color: var(--bg-card-secondary) !important;
}

.json-viewer-wrapper >>> .jv-key {
  color: #60a5fa !important;
}

.json-viewer-wrapper >>> .jv-item.jv-string {
  color: #10b981 !important;
}

.json-viewer-wrapper >>> .jv-item.jv-number {
  color: #f59e0b !important;
}

.json-viewer-wrapper >>> .jv-item.jv-boolean {
  color: var(--primary-start) !important;
}

.json-viewer-wrapper >>> .jv-item.jv-null {
  color: #ef4444 !important;
}
</style>
